import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Edit2, Eye, EyeOff, Upload, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEMA_TYPES = [
  { value: 'Article', label: 'Article (vanlig artikel/guide)' },
  { value: 'HowTo', label: 'HowTo (steg-för-steg)' },
  { value: 'FAQPage', label: 'FAQPage (frågor och svar)' },
];

const CATEGORY_OPTIONS = [
  { value: 'guide', label_sv: 'Guider', label_en: 'Guides' },
  { value: 'how-to', label_sv: 'Steg-för-steg', label_en: 'How-to' },
  { value: 'news', label_sv: 'Nyheter', label_en: 'News' },
  { value: 'faq', label_sv: 'Vanliga frågor', label_en: 'FAQ' },
];

const DocxImportButton = ({ onSuccess, language }) => {
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState('');
  const fileRef = React.useRef(null);
  const pollRef = React.useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setStep(language === 'sv' ? 'Laddar upp dokumentet...' : 'Uploading document...');
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/articles/import/docx`, formData, { headers });
      const jobId = res.data.job_id;
      const startedAt = Date.now();
      pollRef.current = setInterval(async () => {
        if (Date.now() - startedAt > 10 * 60 * 1000) {
          clearInterval(pollRef.current);
          setImporting(false);
          toast.error(language === 'sv' ? 'Importen tog för lång tid – kontrollera artiklarna eller försök igen.' : 'Import timed out – check articles or try again.');
          return;
        }
        try {
          const st = await axios.get(`${API}/articles/import/status/${jobId}`, { headers });
          setStep(st.data.step || '');
          if (st.data.status === 'done') {
            clearInterval(pollRef.current);
            setImporting(false);
            toast.success(language === 'sv' ? `Artikeln "${st.data.title}" publicerad!` : `Article "${st.data.title}" published!`);
            onSuccess();
          } else if (st.data.status === 'error') {
            clearInterval(pollRef.current);
            setImporting(false);
            toast.error((language === 'sv' ? 'Importen misslyckades: ' : 'Import failed: ') + (st.data.error || ''));
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setImporting(false);
      toast.error(err.response?.data?.detail || (language === 'sv' ? 'Uppladdningen misslyckades' : 'Upload failed'));
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept=".docx" className="hidden" onChange={handleFile} data-testid="docx-import-input" />
      <Button
        variant="outline"
        disabled={importing}
        onClick={() => fileRef.current?.click()}
        data-testid="docx-import-button"
        className="border-[#321a27] text-[#321a27] hover:bg-[#321a27] hover:text-white"
      >
        {importing ? (
          <span className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {step || (language === 'sv' ? 'Bearbetar...' : 'Processing...')}
          </span>
        ) : (
          language === 'sv' ? 'Importera Word (auto)' : 'Import Word (auto)'
        )}
      </Button>
    </>
  );
};

const ArticlesTable = ({ articles, products, language, onDelete, onRefresh }) => (
  <div className="bg-white border border-stone-200 overflow-hidden">
    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-stone-600" />
        <h3 className="text-stone-900 font-semibold">
          {language === 'sv' ? 'Kunskapsartiklar' : 'Knowledge Articles'}
        </h3>
        <span className="text-sm text-stone-500">({articles.length})</span>
      </div>
      <div className="flex items-center gap-2">
        <DocxImportButton onSuccess={onRefresh} language={language} />
        <ArticleFormDialog onSuccess={onRefresh} language={language} products={products} />
      </div>
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-stone-50">
            <TableHead>{language === 'sv' ? 'Titel' : 'Title'}</TableHead>
            <TableHead>{language === 'sv' ? 'Kategori' : 'Category'}</TableHead>
            <TableHead>{language === 'sv' ? 'Schema' : 'Schema'}</TableHead>
            <TableHead>{language === 'sv' ? 'Författare' : 'Author'}</TableHead>
            <TableHead>{language === 'sv' ? 'Visningar' : 'Views'}</TableHead>
            <TableHead>{language === 'sv' ? 'Status' : 'Status'}</TableHead>
            <TableHead className="text-right">{language === 'sv' ? 'Åtgärder' : 'Actions'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {articles.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-stone-500 py-8">
                {language === 'sv' ? 'Inga artiklar än. Klicka på "Ny artikel" för att skapa.' : 'No articles yet.'}
              </TableCell>
            </TableRow>
          ) : (
            articles.map((article) => (
              <TableRow key={article.id} className="hover:bg-stone-50">
                <TableCell className="font-medium max-w-md">
                  <div className="truncate">
                    {language === 'sv' ? article.title_sv : (article.title_en || article.title_sv)}
                  </div>
                  <div className="text-xs text-stone-400 font-mono truncate">/{article.slug}</div>
                </TableCell>
                <TableCell>
                  <span className="px-2 py-0.5 bg-stone-100 text-stone-700 text-xs">
                    {CATEGORY_OPTIONS.find((c) => c.value === article.category)?.[language === 'sv' ? 'label_sv' : 'label_en'] || article.category}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs">
                    {article.schema_type || 'Article'}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-stone-600">{article.author || '-'}</TableCell>
                <TableCell className="text-sm text-stone-600">{article.view_count || 0}</TableCell>
                <TableCell>
                  {article.is_published ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                      <Eye className="w-3 h-3" /> {language === 'sv' ? 'Publicerad' : 'Published'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-stone-500 text-xs">
                      <EyeOff className="w-3 h-3" /> {language === 'sv' ? 'Utkast' : 'Draft'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <ArticleFormDialog onSuccess={onRefresh} language={language} editData={article} products={products} />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(article.id)}
                      data-testid={`delete-article-${article.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  </div>
);

const ArticleFormDialog = ({ onSuccess, language, editData = null, products = [] }) => {
  const [open, setOpen] = useState(false);
  const [activeLang, setActiveLang] = useState('sv');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug: '',
    title_sv: '', title_en: '',
    excerpt_sv: '', excerpt_en: '',
    content_sv: '', content_en: '',
    cover_image_url: '',
    author: 'Flowingo Redaktion',
    category: 'guide',
    tags: '',
    related_product_ids: [],
    meta_title_sv: '', meta_title_en: '',
    meta_description_sv: '', meta_description_en: '',
    schema_type: 'Article',
    reading_time_minutes: 5,
    is_published: true,
  });

  useEffect(() => {
    if (editData && open) {
      setForm({
        slug: editData.slug || '',
        title_sv: editData.title_sv || '',
        title_en: editData.title_en || '',
        excerpt_sv: editData.excerpt_sv || '',
        excerpt_en: editData.excerpt_en || '',
        content_sv: editData.content_sv || '',
        content_en: editData.content_en || '',
        cover_image_url: editData.cover_image_url || '',
        author: editData.author || 'Flowingo Redaktion',
        category: editData.category || 'guide',
        tags: (editData.tags || []).join(', '),
        related_product_ids: editData.related_product_ids || [],
        meta_title_sv: editData.meta_title_sv || '',
        meta_title_en: editData.meta_title_en || '',
        meta_description_sv: editData.meta_description_sv || '',
        meta_description_en: editData.meta_description_en || '',
        schema_type: editData.schema_type || 'Article',
        reading_time_minutes: editData.reading_time_minutes || 5,
        is_published: editData.is_published !== false,
      });
    }
  }, [editData, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData);
      setForm((prev) => ({ ...prev, cover_image_url: res.data.url }));
      toast.success('Bild uppladdad');
    } catch {
      toast.error('Kunde inte ladda upp bild');
    } finally {
      setUploading(false);
    }
  };

  const toggleProduct = (productId) => {
    setForm((prev) => ({
      ...prev,
      related_product_ids: prev.related_product_ids.includes(productId)
        ? prev.related_product_ids.filter((id) => id !== productId)
        : [...prev.related_product_ids, productId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.title_sv.trim()) {
      toast.error(language === 'sv' ? 'Titel (sv) krävs' : 'Title (sv) required');
      return;
    }
    if (!form.content_sv.trim()) {
      toast.error(language === 'sv' ? 'Innehåll (sv) krävs' : 'Content (sv) required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        reading_time_minutes: parseInt(form.reading_time_minutes, 10) || 5,
      };
      if (editData) {
        await axios.put(`${API}/articles/${editData.id}`, payload, { headers });
        toast.success(language === 'sv' ? 'Artikel uppdaterad' : 'Article updated');
      } else {
        await axios.post(`${API}/articles`, payload, { headers });
        toast.success(language === 'sv' ? 'Artikel skapad' : 'Article created');
      }
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editData ? (
          <Button variant="ghost" size="icon" data-testid={`edit-article-${editData.id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none" data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Ny artikel' : 'New article'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editData
              ? (language === 'sv' ? 'Redigera artikel' : 'Edit article')
              : (language === 'sv' ? 'Ny kunskapsartikel' : 'New knowledge article')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Language tabs */}
          <div className="flex border-b border-stone-200">
            <button
              type="button"
              onClick={() => setActiveLang('sv')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${activeLang === 'sv' ? 'border-[#321a27] text-[#321a27]' : 'border-transparent text-stone-500'}`}
            >
              Svenska
            </button>
            <button
              type="button"
              onClick={() => setActiveLang('en')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${activeLang === 'en' ? 'border-[#321a27] text-[#321a27]' : 'border-transparent text-stone-500'}`}
            >
              English (valfritt)
            </button>
          </div>

          {/* Title */}
          <div>
            <Label>Titel ({activeLang})</Label>
            <Input
              value={form[`title_${activeLang}`]}
              onChange={(e) => setForm({ ...form, [`title_${activeLang}`]: e.target.value })}
              placeholder="Ex: Skillnaden mellan PN6 och PN10"
              data-testid={`article-title-${activeLang}`}
            />
          </div>

          {/* Slug (only in SV tab to avoid duplication) */}
          {activeLang === 'sv' && (
            <div>
              <Label>Slug (URL) — lämna tom för auto-genereras från titel</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="pn6-vs-pn10-skillnaden"
                className="font-mono text-sm"
              />
            </div>
          )}

          {/* Excerpt */}
          <div>
            <Label>Ingress / Excerpt ({activeLang})</Label>
            <Textarea
              value={form[`excerpt_${activeLang}`]}
              onChange={(e) => setForm({ ...form, [`excerpt_${activeLang}`]: e.target.value })}
              placeholder="Kort beskrivning som visas i listor och i Google-sökresultat..."
              rows={3}
            />
          </div>

          {/* Content - RichTextEditor */}
          <div>
            <Label>Innehåll ({activeLang})</Label>
            <RichTextEditor
              value={form[`content_${activeLang}`]}
              onChange={(html) => setForm({ ...form, [`content_${activeLang}`]: html })}
              placeholder="Skriv din artikel här..."
            />
            <p className="text-xs text-stone-500 mt-1">
              Tips: H2/H3-rubriker används automatiskt för innehållsförteckning och SEO-schema.
              {form.schema_type === 'HowTo' && ' För HowTo: H2/H3 + första stycke blir steg.'}
              {form.schema_type === 'FAQPage' && ' För FAQ: använd H3 för frågor och p för svar.'}
            </p>
          </div>

          {/* Cover Image */}
          {activeLang === 'sv' && (
            <div>
              <Label>Cover-bild</Label>
              {form.cover_image_url ? (
                <div className="flex items-start gap-3">
                  <img src={form.cover_image_url} alt="" className="w-40 h-24 object-cover border border-stone-200" />
                  <div className="flex-1 space-y-2">
                    <Input
                      value={form.cover_image_url}
                      onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                      placeholder="https://..."
                      className="text-xs font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover_image_url: '' })}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Ta bort
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 border border-stone-300 text-sm">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Laddar upp...' : 'Ladda upp'}
                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={uploading} />
                  </label>
                  <span className="text-xs text-stone-500">eller klistra in URL nedan</span>
                  <Input
                    value={form.cover_image_url}
                    onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 text-xs font-mono"
                  />
                </div>
              )}
            </div>
          )}

          {/* Meta tab content (SEO) */}
          <div>
            <Label>Meta title ({activeLang}) — för Google sökresultat</Label>
            <Input
              value={form[`meta_title_${activeLang}`]}
              onChange={(e) => setForm({ ...form, [`meta_title_${activeLang}`]: e.target.value })}
              placeholder="Lämna tom för att använda titeln. Optimal längd: 50-60 tecken."
            />
          </div>
          <div>
            <Label>Meta description ({activeLang})</Label>
            <Textarea
              value={form[`meta_description_${activeLang}`]}
              onChange={(e) => setForm({ ...form, [`meta_description_${activeLang}`]: e.target.value })}
              placeholder="Beskrivning som visas under titeln i Google. Optimal: 140-160 tecken."
              rows={2}
            />
            <p className="text-xs text-stone-500 mt-1">
              {form[`meta_description_${activeLang}`].length}/160 tecken
            </p>
          </div>

          {/* SV-only metadata fields below */}
          {activeLang === 'sv' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-stone-200">
                <div>
                  <Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label_sv}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Schema-typ (för Google)</Label>
                  <Select value={form.schema_type} onValueChange={(v) => setForm({ ...form, schema_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCHEMA_TYPES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Lästid (min)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.reading_time_minutes}
                    onChange={(e) => setForm({ ...form, reading_time_minutes: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Författare</Label>
                  <Input
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Taggar (kommaseparerade)</Label>
                  <Input
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    placeholder="Tryckklass, PN10, Dimensionering"
                  />
                </div>
              </div>

              {/* Related products */}
              {products.length > 0 && (
                <div>
                  <Label>Relaterade produkter ({form.related_product_ids.length} valda)</Label>
                  <div className="border border-stone-200 max-h-40 overflow-y-auto p-2 bg-stone-50">
                    {products.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 px-2 py-1 hover:bg-white text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.related_product_ids.includes(p.id)}
                          onChange={() => toggleProduct(p.id)}
                        />
                        <span className="truncate">{p.name_sv}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-stone-50 border border-stone-200">
                <Switch
                  checked={form.is_published}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  data-testid="article-published-switch"
                />
                <div className="flex-1">
                  <Label className="cursor-pointer">
                    {form.is_published ? 'Publicerad — synlig på sajten' : 'Utkast — endast synlig i admin'}
                  </Label>
                  <p className="text-xs text-stone-500">
                    {form.is_published
                      ? 'Artikeln visas i Kunskapshub och kan hittas via Google.'
                      : 'Artikeln sparas som utkast och visas inte publikt.'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none"
            data-testid="article-save-btn"
          >
            {saving ? 'Sparar...' : (editData ? 'Uppdatera' : 'Skapa artikel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArticlesTable;
export { ArticleFormDialog };
